import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  font-family: 'Cairo', sans-serif;
`;

const ResultCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem 2rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  margin-top: 2rem;
`;

const Logo = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 2rem;
`;

const TestTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
`;

const ScoreContainer = styled.div`
  margin: 2rem 0;
`;

const ScoreCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db, #2980b9);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: 0 8px 25px rgba(52, 152, 219, 0.3);
`;

const ScoreText = styled.div`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
`;

const ScoreLabel = styled.p`
  color: #7f8c8d;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const ResultMessage = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
`;

const MessageTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const MessageText = styled.p`
  color: #7f8c8d;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(231, 76, 60, 0.3);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: transparent;
  color: #3498db;
  border: 2px solid #3498db;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #3498db;
    color: white;
  }
`;

const ServicesList = styled.div`
  margin: 2rem 0;
  text-align: left;
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  
  &::before {
    content: '✓';
    color: #27ae60;
    font-weight: bold;
    margin-right: 1rem;
  }
`;

const ContactInfo = styled.div`
  background: #2c3e50;
  color: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
`;

const ContactTitle = styled.h4`
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const ContactDetails = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
`;

interface LocationState {
  score: number;
  totalQuestions: number;
  testType: 'english' | 'math';
  language: 'ar' | 'en';
}

const AptitudeResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const state = location.state as LocationState;
  
  // Fallback if no state is provided
  if (!state) {
    navigate('/aptitude-test');
    return null;
  }

  const { score, totalQuestions, testType, language } = state;
  const percentage = Math.round((score / totalQuestions) * 100);
  
  const getResultMessage = () => {
    if (percentage >= 80) {
      return {
        title: language === 'ar' ? 'ممتاز!' : 'Excellent!',
        message: language === 'ar' ? 
          'لديك أداء ممتاز! نوصي بالتقدم للجامعات المرموقة.' :
          'You have excellent performance! We recommend applying to prestigious universities.'
      };
    } else if (percentage >= 60) {
      return {
        title: language === 'ar' ? 'جيد جداً' : 'Very Good',
        message: language === 'ar' ? 
          'أداء جيد! يمكنك التحسن أكثر مع المزيد من التدريب.' :
          'Good performance! You can improve more with additional practice.'
      };
    } else {
      return {
        title: language === 'ar' ? 'يحتاج تحسين' : 'Needs Improvement',
        message: language === 'ar' ? 
          'نوصي بالمزيد من التدريب والإعداد قبل التقدم.' :
          'We recommend more practice and preparation before applying.'
      };
    }
  };

  const resultMessage = getResultMessage();

  const content = {
    ar: {
      logo: 'ülearn',
      title: `اختبار ${testType === 'english' ? 'الإنجليزي' : 'الرياضيات'} التجريبي`,
      scoreLabel: `درجتك النهائية من ${totalQuestions}`,
      retakeTest: 'إعادة الاختبار',
      goHome: 'العودة للرئيسية',
      servicesTitle: 'ما هي خدماتنا؟',
      services: [
        'كورت في الرياضيات، الإنجليزي، الكيمياء والفيزياء',
        'مساعدة في اختيار التخصص',
        'دعم في عملية التقديم',
        'تحضير للاختبارات المعيارية'
      ],
      contactTitle: 'تواصل معنا',
      phone: 'هاتف',
      email: 'بريد إلكتروني',
      website: 'موقع إلكتروني',
      faq: 'أسئلة شائعة'
    },
    en: {
      logo: 'ülearn',
      title: `${testType === 'english' ? 'English' : 'Mathematics'} Practice Test`,
      scoreLabel: `Your final score out of ${totalQuestions}`,
      retakeTest: 'Retake Test',
      goHome: 'Go Home',
      servicesTitle: 'What are our services?',
      services: [
        'Courses in Math, English, Chemistry and Physics',
        'Help with major selection',
        'Application process support',
        'Standardized test preparation'
      ],
      contactTitle: 'Contact Us',
      phone: 'Phone',
      email: 'Email',
      website: 'Website',
      faq: 'FAQ'
    }
  };

  const currentContent = content[language];

  const handleRetakeTest = () => {
    navigate('/aptitude-test');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container>
      <ResultCard>
        <Logo>{currentContent.logo}</Logo>
        <TestTitle>{currentContent.title}</TestTitle>
        
        <ScoreContainer>
          <ScoreCircle>
            <ScoreText>{percentage}</ScoreText>
          </ScoreCircle>
          <ScoreLabel>{currentContent.scoreLabel}</ScoreLabel>
        </ScoreContainer>

        <ResultMessage>
          <MessageTitle>{resultMessage.title}</MessageTitle>
          <MessageText>{resultMessage.message}</MessageText>
        </ResultMessage>

        <ActionButton onClick={handleRetakeTest}>
          {currentContent.retakeTest}
        </ActionButton>
        
        <SecondaryButton onClick={handleGoHome}>
          {currentContent.goHome}
        </SecondaryButton>

        <ServicesList>
          <MessageTitle>{currentContent.servicesTitle}</MessageTitle>
          {currentContent.services.map((service, index) => (
            <ServiceItem key={index}>{service}</ServiceItem>
          ))}
        </ServicesList>

        <ContactInfo>
          <ContactTitle>{currentContent.contactTitle}</ContactTitle>
          <ContactDetails>
            <ContactItem>
              📞 {currentContent.phone}: +965 1234 5678
            </ContactItem>
            <ContactItem>
              ✉️ {currentContent.email}: info@ulearn.kw
            </ContactItem>
            <ContactItem>
              🌐 {currentContent.website}: ulearn.kw
            </ContactItem>
            <ContactItem>
              ❓ {currentContent.faq}
            </ContactItem>
          </ContactDetails>
        </ContactInfo>
      </ResultCard>
    </Container>
  );
};

export default AptitudeResults;
